import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  notification,
} from 'antd';
import {
  CloudUploadOutlined,
  KeyOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import Flex from 'components/shared-components/Flex';
import {
  fetchMetaFlowConfigTenants,
  uploadMetaFlowPublicKey,
} from 'services/metaFlowConfigService';

const { Text } = Typography;

const extractUploadErrorMessage = (error) => {
  const payload = error?.response?.data;
  return payload?.message
    || payload?.data?.meta_error?.message
    || error?.message
    || 'Upload failed';
};

const MetaFlowConfig = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingUserId, setUploadingUserId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [uploadFilter, setUploadFilter] = useState('all');
  const [counts, setCounts] = useState({ all: 0, uploaded: 0, pending: 0 });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  const uploadFilterOptions = useMemo(() => ([
    { value: 'all', label: `All tenants (${counts.all})` },
    { value: 'pending', label: `Pending upload (${counts.pending})` },
    { value: 'uploaded', label: `Uploaded (${counts.uploaded})` },
  ]), [counts]);

  const loadTenants = useCallback(async (page = 1, perPage = pagination.pageSize) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
      };

      if (searchText.trim()) {
        params.search = searchText.trim();
      }

      if (uploadFilter === 'uploaded') {
        params.uploaded = 1;
      } else if (uploadFilter === 'pending') {
        params.uploaded = 0;
      }

      const response = await fetchMetaFlowConfigTenants(params);
      setRows(response?.data || []);
      setCounts(response?.counts || { all: 0, uploaded: 0, pending: 0 });
      setPagination({
        current: response?.pagination?.current_page || page,
        pageSize: response?.pagination?.per_page || perPage,
        total: response?.pagination?.total || 0,
      });
    } catch (error) {
      message.error(error?.response?.data?.message || 'Failed to load Meta Flow config');
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, searchText, uploadFilter]);

  useEffect(() => {
    loadTenants(1);
  }, [uploadFilter]);

  const handleSearch = () => {
    loadTenants(1);
  };

  const handleUpload = async (record, force = false) => {
    setUploadingUserId(record.user_id);
    try {
      const response = await uploadMetaFlowPublicKey(record.user_id, force);
      message.success(response?.message || 'Public key uploaded to Meta');
      await loadTenants(pagination.current);
    } catch (error) {
      const errorMessage = extractUploadErrorMessage(error);
      notification.error({
        message: `Upload failed (User ${record.user_id})`,
        description: errorMessage,
        duration: 10,
      });
      await loadTenants(pagination.current);
    } finally {
      setUploadingUserId(null);
    }
  };

  const columns = [
    {
      title: '#',
      width: 70,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      render: (value, record) => value || record.name || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      ellipsis: true,
    },
    {
      title: 'Phone Number ID',
      dataIndex: 'whatsapp_phone_id',
      ellipsis: true,
      render: (value) => value || <Text type="secondary">—</Text>,
    },
    {
      title: 'Key on Meta',
      dataIndex: 'meta_flow_public_key_uploaded',
      width: 130,
      render: (uploaded) => (
        <Tag color={uploaded ? 'green' : 'orange'}>
          {uploaded ? 'Uploaded' : 'Pending'}
        </Tag>
      ),
    },
    {
      title: 'Onboarding',
      dataIndex: 'onboarding_status',
      width: 140,
      render: (value) => value || '—',
    },
    {
      title: 'Last error',
      dataIndex: 'meta_flow_public_key_upload_error',
      width: 280,
      ellipsis: true,
      render: (value) => {
        if (!value) {
          return <Text type="secondary">—</Text>;
        }

        return (
          <Tooltip title={value}>
            <Text type="danger">{value}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 220,
      render: (_, record) => {
        const isUploading = uploadingUserId === record.user_id;
        const canUpload = record.upload_eligible;

        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<CloudUploadOutlined />}
              loading={isUploading}
              disabled={!canUpload}
              onClick={() => handleUpload(record, false)}
            >
              Upload
            </Button>
            {record.meta_flow_public_key_uploaded ? (
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={isUploading}
                disabled={!canUpload}
                onClick={() => handleUpload(record, true)}
              >
                Re-upload
              </Button>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <Card
      title={(
        <Flex alignItems="center" style={{ gap: 8 }}>
          <KeyOutlined />
          <span>Meta Flow Config</span>
          <Text type="secondary">
            Upload the platform public key to Meta for each tenant phone number.
          </Text>
        </Flex>
      )}
      extra={(
        <Flex gap={10} wrap="wrap">
          <Input
            allowClear
            placeholder="Search company, email, phone id"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 260 }}
          />
          <Select
            value={uploadFilter}
            options={uploadFilterOptions}
            onChange={setUploadFilter}
            style={{ width: 200 }}
          />
          <Button icon={<SearchOutlined />} onClick={handleSearch}>
            Search
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => loadTenants(pagination.current)}>
            Refresh
          </Button>
        </Flex>
      )}
    >
      <Table
        rowKey="user_id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1280 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => loadTenants(page, pageSize),
        }}
      />
    </Card>
  );
};

export default MetaFlowConfig;
